import React, { useEffect, useState, useCallback } from "react";
import ProgressTracker from "../../../../../utils/ProgressTracker";
import { Checkbox } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import {
  markComplete,
  markDescriptionComplete,
  markNonComplete,
  markNotDescriptionComplete,
  updateTaskHours,
} from "../../../../../store/slices/ProjectOngoing/projectOngoingSlice";
import { useSnackbar } from "notistack";
import { fetchWorkEntriesProjectTracking } from "../../../../../store/slices/workentriesSlice";
import LoadingSpinner from "../../../../../components/common/loader/LoadingSpinner";
import { fetchDescriptionTasksProjectTracking } from "../../../../../store/slices/DescriptionTask";
import {
  updateOngoingDescription,
} from "../../../../../store/slices/ProjectOngoing/ProjectOngoingUpdateSlice";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import DateConversion from "../../../../../utils/DateConversion";
import {
  formatAmountComma,
  formatAmountForInput,
  validateAmountValue,
  parseAmountNumber,
} from "../../../../../utils/decimalAmountUtils";
import { useLoggedUser } from "../../../../../utils/useLoggedUser";
import useToolGuideSync from "../../../../../features/toolGuide/useToolGuideSync";
import { MANAGER_BUCKET_KEYS } from "../../../../../features/toolGuide/toolGuideConfig";
import {
  resolveTrainingAssignedDate,
  resolveTrainingEndDate,
  filterInstallationOngoingOtherExpenseDisplayRows,
} from "../../../../../utils/trainingOtherExpenseSync";
// import { useRef } from 'react';

const SelfTaskTrackManager = ({
  handlerefresh,
  matchedManagerWorkEntries,
  setAggregateProgressMap,
  row,
  setIsLoading,
  matchedHardwarePartsManager,
  matchedHardwareDescriptionsPartsManager,
  showListState,
  isViewOnly = false,
}) => {
  const dispatch = useDispatch();
  const { syncToolGuideStep } = useToolGuideSync(
    MANAGER_BUCKET_KEYS.PROJECT_ONGOING,
    "L4_PM"
  );
  const loggedUser = useLoggedUser();
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } =
    DateConversion();
  const currency = useCurrencySymbol();
  const { mytrainings } = useSelector((state) => state.myTrainings);

  const { enqueueSnackbar } = useSnackbar();
  const [checkedRows] = useState({});
  const [loadingRows, setLoadingRows] = useState({});
  const [managerWorkEntries, setManagerWorkEntries] = useState([]);
  const formatTwoDecimals = (value) =>
    (Math.trunc((Number(value) || 0) * 100) / 100).toFixed(2);

  useEffect(() => {
    setManagerWorkEntries(matchedManagerWorkEntries);
  }, [matchedManagerWorkEntries]);

  const [editedTask, setEditedTask] = useState({});
  const [editedNotes, setEditedNotes] = useState({});

  useEffect(() => {
    if (!managerWorkEntries?.length) {
      setAggregateProgressMap({});
      return;
    }
    const progressMap = {};
    managerWorkEntries?.forEach((entry) => {
      const projectId = entry.projectid;
      const currentHours =
        editedTask?.taskId === entry._id
          ? Number(editedTask.value) || 0
          : Number(entry.hoursspent) || 0;

      if (!progressMap[projectId]) {
        progressMap[projectId] = { totalAssigned: 0, totalSpent: 0 };
      }

      progressMap[projectId].totalAssigned += Number(entry.hoursassigned || 0);
      progressMap[projectId].totalSpent += currentHours;
    });

    // calculate percentage for each project
    const aggregateMap = {};
    Object.keys(progressMap).forEach((projectId) => {
      const { totalAssigned, totalSpent } = progressMap[projectId];
      aggregateMap[projectId] =
        totalAssigned > 0
          ? Math.min((totalSpent / totalAssigned) * 100, 100)
          : 0;
    });

    setAggregateProgressMap(aggregateMap);
  }, [managerWorkEntries, editedTask, setAggregateProgressMap]);

  const handleUpdateHours = async (taskId) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const value = editedTask[taskId]; // get the value for this specific task

    if (!taskId) return;
    const projectid = row?._id;
    const updatedData = {
      hoursspent: Number(value),
    };
    try {
      await dispatch(
        updateTaskHours({
          projectid,
          taskownerid: taskId,
          updatedData,
        })
      ).unwrap();
    } catch (err) {
      enqueueSnackbar(err, { variant: "error" });
    } finally {
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    }
  };
  const handleUpdateNotes = async (taskId) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const value = editedNotes[taskId]; // get the value for this specific task

    if (!taskId) return;
    const projectid = row?._id;
    const updatedData = {
      notes: value,
    };
    try {
      await dispatch(
        updateTaskHours({
          projectid,
          taskownerid: taskId,
          updatedData,
        })
      ).unwrap();
    } catch (err) {
      enqueueSnackbar(err, { variant: "error" });
    } finally {
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    }
  };
  const handleTaskMarkComplete = async (entry) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markComplete({ projectid, taskid }));
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Task Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const handleMarkNonComplete = async (entry) => {
    if (isViewOnly) return;
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markNonComplete({ projectid, taskid }));
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Task Not-Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  // Mark all entries in a consolidated description group complete / not complete (manager + operation person same description)
  const handlemarkDescriptionCompleteForGroup = async (entriesToUpdate, rowIndex) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const projectid = row?._id;
    const toUpdate = (entriesToUpdate || []).filter((e) => e?._id);
    setLoadingRows((prev) => ({ ...prev, [rowIndex]: true }));
    setIsLoading(true);
    try {
      for (const entry of toUpdate) {
        await dispatch(markDescriptionComplete({ projectid, taskid: entry._id }));
      }
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Description Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [rowIndex]: false }));
      setIsLoading(false);
    }
  };

  const handlemarkNotDescriptionCompleteForGroup = async (entriesToUpdate, rowIndex) => {
    if (isViewOnly) return;
    const projectid = row?._id;
    const toUpdate = (entriesToUpdate || []).filter((e) => e?._id);
    setLoadingRows((prev) => ({ ...prev, [rowIndex]: true }));
    setIsLoading(true);
    try {
      for (const entry of toUpdate) {
        await dispatch(markNotDescriptionComplete({ projectid, taskid: entry._id }));
      }
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Description Not-Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [rowIndex]: false }));
      setIsLoading(false);
    }
  };

  const [updatedDateDescription, setUpdatedDateDescription] = useState({});

  const handleDescriptionDateSubmit = useCallback(
    async (entry) => {
      syncToolGuideStep("view-details");
      const matchedEntry = updatedDateDescription[entry._id] || {};
      const matchedStartDate = matchedEntry.startdate || null;
      const matchedEndDate = matchedEntry.enddate || null;

      // Use manager's actual value from source list; do not save consolidated sum (display-only)
      const rawEntry = (matchedHardwareDescriptionsPartsManager || []).find((e) => e._id === entry._id);
      const valueToSave = rawEntry != null ? rawEntry.value : entry.value;

      const updatedData = {
        description: entry.description,
        value: valueToSave,
        notes: entry.notes,
        typeservice: entry.typeservice,
        startdate: matchedStartDate ?? entry.startdate ?? null,
        enddate: matchedEndDate ?? entry.enddate ?? null,
      };
      try {
        dispatch(
          updateOngoingDescription({
            projectId: row?._id,
            id: entry._id,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
      } catch {
        enqueueSnackbar("Failed To Add Date", { variant: "error" });
      } finally {
        handlerefresh();
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
        setIsLoading(false);
      }
    },
    [
      updatedDateDescription,
      row,
      dispatch,
      enqueueSnackbar,
      handlerefresh,
      setLoadingRows,
      setIsLoading,
      matchedHardwareDescriptionsPartsManager,
      syncToolGuideStep,
    ]
  );

  const handleDescriptionDateChange = useCallback(
    (date, field, entry) => {
      if (isViewOnly) return;
      setUpdatedDateDescription((prev) => ({
        ...prev,
        [entry._id]: {
          ...prev[entry._id],
          [field]: date,
        },
      }));
      if (field === "enddate") {
        const newentry = {
          ...entry,
          enddate: date,
        };
        handleDescriptionDateSubmit(newentry);
      }
    },
    [handleDescriptionDateSubmit, isViewOnly]
  );

  const [updatedDateWorkEntries, setUpdatedDateWorkEntries] = useState({});

  const handleWorkEntriesDateSubmit = useCallback(
    async (entry) => {
      syncToolGuideStep("view-details");
      const matchedEntry = updatedDateWorkEntries[entry._id] || {};
      const matchedStartDate = matchedEntry.startdate || null;
      const matchedEndDate = matchedEntry.enddate || null;

      const updatedData = {
        startdate: matchedStartDate ?? entry.startdate ?? null,
        enddate: matchedEndDate ?? entry.enddate ?? null,
      };
      try {
        await dispatch(
          updateTaskHours({
            projectid: row?._id,
            taskownerid: entry?._id,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
      } catch {
        enqueueSnackbar("Failed To Add Date", { variant: "error" });
      } finally {
        handlerefresh();
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
        setIsLoading(false);
      }
    },
    [
      updatedDateWorkEntries,
      handlerefresh,
      row,
      setIsLoading,
      dispatch,
      enqueueSnackbar,
      syncToolGuideStep,
    ]
  );

  const workEntriesDateChange = useCallback(
    (date, field, entry) => {
      if (isViewOnly) return;
      setUpdatedDateWorkEntries((prev) => ({
        ...prev,
        [entry._id]: {
          ...prev[entry._id],
          [field]: date,
        },
      }));
      if (field === "enddate") {
        const newentry = {
          ...entry,
          enddate: date,
        };
        handleWorkEntriesDateSubmit(newentry);
      }
    },
    [handleWorkEntriesDateSubmit, isViewOnly]
  );

  const [editedDescription, setEditedDescription] = useState({});
  const [editedDescriptionNotes, setEditedDescriptionNotes] = useState({});

  const isOverdueIncomplete = (entry) => {
    const isIncomplete =
      entry?.isTaskComplete === false || entry?.isTaskcomplete === false;
    if (!isIncomplete || !entry?.enddate) return false;
    const endDate = new Date(entry.enddate);
    if (isNaN(endDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  // Self manager only: one row per manager entry (no consolidation/grouping with operation). Use only manager's data for display and calculations.
  const [filteredDescriptionList, setFilteredDescriptionList] = useState([]);
  useEffect(() => {
    const resolveRefId = (ref) => {
      if (ref == null || ref === "") return "";
      if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
      return String(ref).trim();
    };

    const trainingsList = Array.isArray(mytrainings?.trainings)
      ? mytrainings.trainings
      : Array.isArray(mytrainings)
        ? mytrainings
        : [];

    const projectId = String(row?._id || row?.ProjectID || "").trim().toLowerCase();
    const quotationId = String(row?.quotationId || row?.quotationID || row?.quotationid || "").trim();
    const projectName = String(row?.ProjectName || row?.projectname || "").trim().toLowerCase();
    const leadId = String(row?.leadid || row?.leadId || row?.LeadId || row?.LeadID || "").trim();

    const trainingRows = (() => {
      if (!loggedUser?._id) return [];
      const assigneeId = resolveRefId(loggedUser._id);
      const matched = trainingsList.filter((t) => {
        const assignedTo = resolveRefId(t?.assignedto ?? t?.assignedTo);
        if (!assignedTo || assignedTo !== assigneeId) return false;

        const tProjectId = resolveRefId(t?.projectid ?? t?.projectId ?? t?.project).toLowerCase();
        const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
        const tProjectName = String(t?.projectname || "").trim().toLowerCase();
        const tLeadId = String(t?.leadid ?? t?.leadId ?? "").trim();

        return (
          (projectId && tProjectId && projectId === tProjectId) ||
          (quotationId && tQuotationId && quotationId === tQuotationId) ||
          (projectName && tProjectName && projectName === tProjectName) ||
          (leadId && tLeadId && leadId === tLeadId)
        );
      });

      const normalized = matched.map((t) => {
        const amount = Number(
          String(t?.trainingcost ?? t?.trainingCost ?? 0)
            .toString()
            .replace(/,/g, "")
        );
        const notes =
          t?.notes ??
          t?.note ??
          "";
        const startdate = resolveTrainingAssignedDate(t, row?.DateRequested);
        const enddate = resolveTrainingEndDate(t);
        return {
          amount: Number.isFinite(amount) ? amount : 0,
          notes: String(notes || "").trim(),
          startdate,
          enddate,
        };
      });

      const total = normalized.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
      const joinedNotes = Array.from(new Set(normalized.map((x) => x.notes).filter(Boolean))).join("; ");
      if (total <= 0 && !joinedNotes) return [];

      return [
        {
          _id: `training_cost_${assigneeId}`,
          taskownerid: assigneeId,
          description: "Training Cost",
          value: total,
          notes: joinedNotes,
          startdate: normalized.map((x) => x.startdate).find(Boolean) ?? row?.DateRequested ?? null,
          enddate: normalized.map((x) => x.enddate).find(Boolean) ?? null,
          isTaskComplete: true,
          expenses: total,
          _isTrainingCost: true,
        },
      ];
    })();

    const managerEntries = filterInstallationOngoingOtherExpenseDisplayRows(
      matchedHardwareDescriptionsPartsManager?.filter(
        (e) => String(e?.taskownerid || "") === String(loggedUser?._id || "")
      ) || [],
      row
    );
    const list = [...managerEntries, ...trainingRows].map((entry) => ({
      ...entry,
      allEntriesInGroup: [entry],
    }));
    setFilteredDescriptionList(list);
  }, [matchedHardwareDescriptionsPartsManager, loggedUser?._id, mytrainings, row]);

  const handleDescriptionExpensesUpdate = async (entryId) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const value = editedDescription[entryId]; // get the value for this specific task
    if (!entryId) return;

    const updatedData = {
      expenses: parseAmountNumber(value),
    };
    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  const handleDescriptionNotesUpdate = async (entryId) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const value = editedDescriptionNotes[entryId]; // get the value for this specific task
    if (!entryId) return;
    const updatedData = {
      notes: value,
    };
    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  return (
    <div>
      <>
        {showListState === "tasktable" && (
          <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
            <div className="z-[50] flex bg-[#e8e8e8] text-black text-[10px] sticky top-0  font-semibold break-all whitespace-normal ">
              <div className="p-1 w-[10%]  flex justify-center items-center">
                Tasks
              </div>
              <div className="p-0.5 w-[12%] flex justify-center items-center break-words whitespace-normal">
                Hours Assigned
              </div>
              <div className="p-0.5 w-[10%] flex justify-center items-center">
                Hours Spent
              </div>
              <div className="p-0.5 w-[14%] flex justify-center items-center">
                Start Date
              </div>
              <div className="p-0.5 w-[14%] flex justify-center items-center">
                End Date
              </div>
              <div className="p-1 w-[10%] flex justify-center items-center">
                Notes
              </div>
              <div className="p-1 w-[24%] flex justify-center items-center">
                Tracker
              </div>
              <div className="p-0.5 w-[6%] text-center">
                Action
              </div>
            </div>
            <div className="flex text-[10px] break-all whitespace-normal max-h-14">
              <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center items-center">
                {"Total"}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[12%] flex justify-center items-center">
                {managerWorkEntries.reduce(
                  (sum, item) => sum + (item?.hoursassigned || 0),
                  0
                )}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center items-center">
                {formatTwoDecimals(
                  managerWorkEntries.reduce(
                    (sum, item) => sum + (item?.hoursspent || 0),
                    0
                  )
                )}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[14%] flex justify-center items-center"></div>
              <div className="p-1 border-b border-[#dee2e6] w-[14%] flex justify-center items-center"></div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] flex justify-center items-center"></div>
              <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                {managerWorkEntries.every((entry) => entry.isTaskComplete) ? (
                  <>
                    {(() => {
                      const hoursAssigned = managerWorkEntries.reduce(
                        (sum, item) => sum + (item?.hoursassigned || 0),
                        0
                      );
                      const hoursSpent = managerWorkEntries.reduce(
                        (sum, item) => sum + (item?.hoursspent || 0),
                        0
                      );

                      const trackerPercentage =
                        hoursAssigned > 0
                          ? (hoursSpent / hoursAssigned) * 100
                          : 0;

                      const displayValue =
                        trackerPercentage < 100 ? 100 : trackerPercentage;

                      return <ProgressTracker value={displayValue} />;
                    })()}
                  </>
                ) : (
                  <>
                    {(() => {
                      const hoursAssigned = managerWorkEntries.reduce(
                        (sum, item) => sum + (item?.hoursassigned || 0),
                        0
                      );
                      const hoursSpent = managerWorkEntries.reduce(
                        (sum, item) => sum + (item?.hoursspent || 0),
                        0
                      );
                      const trackerPercentage =
                        hoursAssigned > 0
                          ? (hoursSpent / hoursAssigned) * 100
                          : 0;
                      return <ProgressTracker value={trackerPercentage} />;
                    })()}
                  </>
                )}
              </div>
              <div className="p-0.5 border-b border-[#dee2e6] w-[6%] text-center"></div>
            </div>

            {managerWorkEntries.length > 0 ? (
              managerWorkEntries.map((entry, index) => (
                (() => {
                  const showOverdueWarning = isOverdueIncomplete(entry);
                  return (
                <div
                  key={entry}
                  title={showOverdueWarning ? "end date is passed" : ""}
                  className={`flex break-all whitespace-normal cursor-pointer ${
                    ""
                  }`}
                >
                  <div className="p-0.5 border-b border-[#dee2e6] w-[10%] overflow-auto flex flex-col justify-center items-center">
                    <p className="text-[10px]" title={`${entry.task}`}>
                      {" "}
                      {entry.task}{" "}
                    </p>
                    {(() => {
                      if (entry.enddate && !entry.isTaskComplete) {
                        const endDate = new Date(entry.enddate);
                        const today = new Date();
                        const twoDaysFromNow = new Date();
                        twoDaysFromNow.setDate(today.getDate() + 2);

                        // Set hours to 0 to compare only dates
                        today.setHours(0, 0, 0, 0);
                        endDate.setHours(0, 0, 0, 0);
                        twoDaysFromNow.setHours(0, 0, 0, 0);
                        if (endDate >= today && endDate <= twoDaysFromNow) {
                          return (
                            <span
                              title="You have upcoming deadline 2 days"
                              className="text-orange-300 text-xs"
                            >
                              {" "}
                              ⏳{" "}
                            </span>
                          );
                        }
                      }
                      return null;
                    })()}
                  </div>
                  <div className="p-0.5 text-[10px] border-b border-[#dee2e6] w-[12%] flex justify-center items-center">
                    {entry.hoursassigned}
                  </div>
                  <div className="p-0.5 text-[10px] border-b border-[#dee2e6] w-[10%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <span>{entry.hoursspent || ""}</span>
                    ) : (
                      <input
                        type="text"
                        value={
                          editedTask[entry._id] ?? entry.hoursspent ?? ""
                        }
                        placeholder="enter hours"
                        disabled={isViewOnly}
                        className={`border border-gray-200 w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                        onChange={(e) => {
                          if (isViewOnly) return;
                          const value = e.target.value;
                          const parsedValue = Number(value);

                          if (
                            value !== "" &&
                            parsedValue > Number(entry.hoursassigned || 0)
                          ) {
                            enqueueSnackbar(
                              "Hours Spent is exceeding the Hours Assigned",
                              {
                                variant: "error",
                              }
                            );
                          }
                          // Allow decimal hours up to 2 digits after dot
                          if (!/^\d*\.?\d{0,2}$/.test(value)) return;
                          if (value !== "" && parsedValue < 0) return;

                          setEditedTask((prev) => ({
                            ...prev,
                            [entry._id]: value,
                          }));
                        }}
                        onBlur={() => handleUpdateHours(entry._id)}
                      />
                    )}
                  </div>
                  <div className="p-0.5 text-[10px] border-b border-[#dee2e6] w-[14%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <div className="text-black">
                        {entry.startdate
                          ? formatDateByCountry(entry.startdate)
                          : ""}
                      </div>
                    ) : (
                      <DatePicker
                        dateFormat={getDateFormat()}
                        disabled={isViewOnly}
                        className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={
                          entry.startdate
                            ? new Date(entry.startdate)
                            : row?.DateRequested
                              ? new Date(row.DateRequested)
                              : null
                        }
                        maxDate={new Date()}
                        selected={
                          updatedDateWorkEntries[entry._id]?.startdate
                            ? updatedDateWorkEntries[entry._id].startdate
                            : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                        }
                        onChange={(date) => {
                          if (isViewOnly) return;
                          workEntriesDateChange(date, "startdate", entry);
                        }}
                      />
                    )}
                  </div>
                  <div className="p-1 text-[10px] border-b border-[#dee2e6] w-[14%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <div className="text-black">
                        {entry.enddate
                          ? formatDateByCountry(entry.enddate)
                          : ""}
                      </div>
                    ) : (
                      <DatePicker
                        dateFormat={getDateFormat()}
                        disabled={isViewOnly}
                        className={`w-full h-full text-center outline-none border p-1 ${
                          showOverdueWarning
                            ? "border-orange-400"
                            : "border-gray-300"
                        } rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={
                          updatedDateWorkEntries[entry._id]?.startdate
                            ? new Date(
                              updatedDateWorkEntries[entry._id].startdate
                            )
                            : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                        }
                        selected={
                          updatedDateWorkEntries[entry._id]?.enddate
                            ? updatedDateWorkEntries[entry._id].enddate
                            : entry.enddate
                              ? new Date(entry.enddate)
                              : null
                        }
                        onChange={(date) => {
                          if (isViewOnly) return;
                          workEntriesDateChange(date, "enddate", entry);
                        }}
                      />
                    )}
                  </div>
                  <div
                    title={`${entry.notes}`}
                    className="p-1 border-b text-[10px] border-[#dee2e6] w-[10%] flex justify-center items-center break-words whitespace-normal overflow-auto"
                  >
                    {/* {entry.notes || ""} */}
                    {entry.isTaskComplete ? (
                      <span>{entry.notes || ""}</span>
                    ) : (
                      <input
                        type="text"
                        value={editedNotes[entry._id] ?? entry.notes ?? ""}
                        placeholder="enter note"
                        disabled={isViewOnly}
                        className={`border border-gray-200 w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                        onChange={(e) => {
                          if (isViewOnly) return;
                          const value = e.target.value;
                          setEditedNotes((prev) => ({
                            ...prev,
                            [entry._id]: value,
                          }));
                        }}
                        onBlur={() => handleUpdateNotes(entry._id)}
                      />
                    )}
                  </div>
                  <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center ">
                    {entry.isTaskComplete ? (
                      <ProgressTracker
                        value={(() => {
                          const rawValue =
                            ((Number(entry.hoursspent) || 0) /
                              entry.hoursassigned) *
                            100;
                          return rawValue < 100 ? 100 : rawValue;
                        })()}
                      />
                    ) : (
                      <ProgressTracker
                        value={
                          ((Number(
                            editedTask[entry._id] ?? entry.hoursspent
                          ) || 0) /
                            entry.hoursassigned) *
                          100
                        }
                      />
                    )}
                  </div>
                  <div
                    className={` p-0.5 border-b border-[#dee2e6] w-[6%] flex justify-center items-center 
                      ${row.Assignedperson.every(
                      (person) => person.isProjectComplete === true
                    )
                        ? "cursor-default"
                        : ""
                      }`}
                  >
                    {loadingRows[index] ? (
                      <LoadingSpinner />
                    ) : (
                      <Checkbox
                        checked={entry.isTaskComplete || !!checkedRows[index]}
                        disabled={
                          isViewOnly ||
                          row.Assignedperson?.every(
                            (person) => person.isProjectComplete === true
                          ) ||
                          ((Number(
                            editedTask[entry._id] ?? entry.hoursspent
                          ) || 0) /
                            entry.hoursassigned) *
                          100 <
                          1
                        }
                        onChange={(e) => {
                          if (isViewOnly) return;
                          const isChecked = e.target.checked;
                          const currentHours =
                            editedTask[entry._id] !== undefined
                              ? Number(editedTask[entry._id])
                              : Number(entry.hoursspent);

                          const startDate =
                            updatedDateWorkEntries[entry._id]?.startdate ||
                            entry.startdate;
                          const endDate =
                            updatedDateWorkEntries[entry._id]?.enddate ||
                            entry.enddate;

                          if (isChecked) {
                            if (!currentHours || currentHours <= 0) {
                              enqueueSnackbar("Please Enter Hours Spent", {
                                variant: "error",
                              });
                              e.preventDefault();
                              return;
                            }
                            if (!startDate || !endDate) {
                              enqueueSnackbar("Please Select End Date", {
                                variant: "error",
                              });
                              return;
                            }
                            handleTaskMarkComplete(entry, index);
                            handleWorkEntriesDateSubmit(entry);
                          } else {
                            handleMarkNonComplete(entry);
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              );
                })()
              ))
            ) : (
              <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                <p className="text-red-500 text-center">
                  {" "}
                  No Tracking Task Entries Found
                </p>
              </div>
            )}
          </div>
        )}

        {showListState === "otherexpenses" && (
          <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
            <div className="z-[50] sticky top-0 flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
              <div className="p-1 w-[10%] flex justify-center items-center">
                Description 
              </div>
              <div className="p-0.5 w-[11%] flex justify-center items-center">
                Amount ({currency})
              </div>
              <div className="p-0.5 w-[14%] flex justify-center items-center">
                Start Date
              </div>
              <div className="p-0.5 w-[14%] flex justify-center items-center">
                End Date
              </div>
              <div className="p-0.5 w-[11%] flex justify-center items-center">
                Expenses ({currency})
              </div>
              <div className="p-0.5 w-[10%] flex justify-center items-center">
                Notes
              </div>
              <div className="p-1  w-[24%] flex justify-center items-center">
                Tracker
              </div>
              <div className="p-1  w-[6%] flex justify-center items-center">
                Action
              </div>
            </div>

            <div className="flex text-[10px] break-all whitespace-normal max-h-14">
              <div className="p-1 text-center border-b border-[#dee2e6] w-[10%] flex justify-center items-center">
                {"Total"}
              </div>
              <div className="p-0.5 border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                {formatAmountComma(
                  filteredDescriptionList.reduce(
                    (sum, item) => sum + parseAmountNumber(item?.value),
                    0
                  )
                )}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[14%] text-center"></div>
              <div className="p-1 border-b border-[#dee2e6] w-[14%] text-center"></div>
              <div className="p-1 border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                {formatAmountComma(
                  filteredDescriptionList.reduce(
                    (sum, grp) =>
                      sum +
                      (grp.allEntriesInGroup || [grp]).reduce(
                        (s, e) => s + parseAmountNumber(e?.expenses),
                        0
                      ),
                    0
                  )
                )}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
              <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                {filteredDescriptionList.length > 0 &&
                filteredDescriptionList.every((grp) =>
                  (grp.allEntriesInGroup || [grp]).every((e) => e?.isTaskComplete)
                ) ? (
                  <>
                    {(() => {
                      const hoursAssigned = filteredDescriptionList.reduce(
                        (sum, item) => sum + (item?.value || 0),
                        0
                      );
                      const hoursSpent = filteredDescriptionList.reduce(
                        (sum, grp) =>
                          sum +
                          (grp.allEntriesInGroup || [grp]).reduce(
                            (s, e) => s + Number(e?.expenses || 0),
                            0
                          ),
                        0
                      );

                      const trackerPercentage =
                        hoursAssigned > 0
                          ? (hoursSpent / hoursAssigned) * 100
                          : 0;

                      const displayValue =
                        trackerPercentage < 100 ? 100 : trackerPercentage;

                      return <ProgressTracker value={displayValue} />;
                    })()}
                  </>
                ) : (
                  <>
                    {(() => {
                      const previousValue = filteredDescriptionList.reduce(
                        (sum, item) => sum + (item?.value || 0),
                        0
                      );
                      const addedExpenses = filteredDescriptionList.reduce(
                        (sum, grp) =>
                          sum +
                          (grp.allEntriesInGroup || [grp]).reduce(
                            (s, e) => s + Number(e?.expenses || 0),
                            0
                          ),
                        0
                      );
                      const trackerPercentage =
                        previousValue > 0
                          ? (addedExpenses / previousValue) * 100
                          : 0;
                      return <ProgressTracker value={trackerPercentage} />;
                    })()}
                  </>
                )}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[6%] text-center"></div>
            </div>

            {filteredDescriptionList.length > 0 ? (
              filteredDescriptionList.map((entry, index) => {
                // Self manager only: one row per entry; checkbox marks only this entry
                const entriesInGroup = entry.allEntriesInGroup || [entry];
                const isGroupComplete = entriesInGroup.every((e) => e?.isTaskComplete);
                const showOverdueWarning = isOverdueIncomplete(entry);
                return (
                <div
                  key={entry}
                  title={showOverdueWarning ? "end date is passed" : ""}
                  className={`flex text-[10px] break-all whitespace-normal max-h-14 cursor-pointer ${
                    ""
                  }`}
                >
                  <div
                    className="p-0.5 border-b border-[#dee2e6] w-[10%] overflow-auto max-h-14 flex justify-center items-center"
                    title={`${entry.description}`}
                  >
                    {entry.description}
                  </div>
                  <div className="p-0.5 border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                    {formatAmountComma(entry.value)}
                  </div>
                  <div className="p-0.5 border-b border-[#dee2e6] w-[14%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <div className="text-black">
                        {entry.startdate
                          ? formatDateByCountry(entry.startdate)
                          : ""}
                      </div>
                    ) : (
                      <DatePicker
                        dateFormat={getDateFormat()}
                        disabled={isViewOnly}
                        className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={
                          entry.startdate
                            ? new Date(entry.startdate)
                            : row?.DateRequested
                              ? new Date(row.DateRequested)
                              : null
                        }
                        maxDate={new Date()}
                        selected={
                          updatedDateDescription[entry._id]?.startdate
                            ? updatedDateDescription[entry._id].startdate
                            : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                        }
                        onChange={(date) => {
                          if (isViewOnly) return;
                          handleDescriptionDateChange(
                            date,
                            "startdate",
                            entry
                          );
                        }}
                      />
                    )}
                  </div>
                  <div className="p-0.5 border-b border-[#dee2e6] w-[14%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      <div className="text-black">
                        {entry.enddate
                          ? formatDateByCountry(entry.enddate)
                          : ""}
                      </div>
                    ) : (
                      <DatePicker
                        dateFormat={getDateFormat()}
                        disabled={isViewOnly}
                        className={`w-full h-full text-center outline-none border p-1 ${
                          showOverdueWarning
                            ? "border-orange-400"
                            : "border-gray-300"
                        } rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={
                          updatedDateDescription[entry._id]?.startdate
                            ? new Date(
                              updatedDateDescription[entry._id].startdate
                            )
                            : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                        }
                        selected={
                          updatedDateDescription[entry._id]?.enddate
                            ? updatedDateDescription[entry._id].enddate
                            : entry.enddate
                              ? new Date(entry.enddate)
                              : null
                        }
                        onChange={(date) => {
                          if (isViewOnly) return;
                          handleDescriptionDateChange(date, "enddate", entry);
                        }}
                      />
                    )}
                  </div>

                  <div className="p-0.5 border-b border-[#dee2e6] w-[11%] flex justify-center items-center">
                    {entry.isTaskComplete ? (
                      // 👇 Just show saved hours if complete
                      <span>{formatAmountComma(entry.expenses) || ""}</span>
                    ) : (
                      // 👇 Editable input if not complete
                      <input
                        type="text"
                        value={
                          editedDescription[entry._id] !== undefined
                            ? editedDescription[entry._id]
                            : formatAmountForInput(entry.expenses) ?? ""
                        }
                        placeholder="enter expense"
                        disabled={isViewOnly}
                        className={`border border-gray-200 w-full rounded-md max-w-full p-1 text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                        onChange={(e) => {
                          if (isViewOnly) return;
                          const value = validateAmountValue(e.target.value);
                          const amountLimit = parseAmountNumber(entry.value);
                          const expenseNum = parseAmountNumber(value);
                          if (
                            value !== "" &&
                            amountLimit > 0 &&
                            expenseNum > amountLimit
                          ) {
                            enqueueSnackbar(
                              "Expenses is exceeding the Amount",
                              {
                                variant: "error",
                              }
                            );
                          }
                          setEditedDescription((prev) => ({
                            ...prev,
                            [entry._id]: value, // store by task id
                          }));
                        }}
                        onBlur={() =>
                          handleDescriptionExpensesUpdate(entry._id)
                        }
                      />
                    )}
                  </div>
                  <div
                    title={`${entry.notes}`}
                    className="p-0.5 border-b border-[#dee2e6] w-[10%] flex justify-center items-center break-words whitespace-normal max-h-14 overflow-auto"
                  >
                    {/* {entry.notes || ""} */}
                    {entry.isTaskComplete ? (
                      <span>{entry.notes || ""}</span>
                    ) : (
                      <input
                        type="text"
                        value={
                          editedDescriptionNotes[entry._id] ??
                          entry.notes ??
                          ""
                        }
                        placeholder="enter note"
                        disabled={isViewOnly}
                        className={`border border-gray-200 w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                        onChange={(e) => {
                          if (isViewOnly) return;
                          const value = e.target.value;
                          setEditedDescriptionNotes((prev) => ({
                            ...prev,
                            [entry._id]: value,
                          }));
                        }}
                        onBlur={() => handleDescriptionNotesUpdate(entry._id)}
                      />
                    )}
                  </div>
                  <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                    {entry.isTaskComplete ? (
                      <ProgressTracker
                        value={(() => {
                          const rawValue =
                            ((Number(
                              editedDescription[entry._id] ?? entry.expenses
                            ) || 0) /
                              entry.value) *
                            100;
                          return rawValue < 100 ? 100 : rawValue;
                        })()}
                      />
                    ) : (
                      <ProgressTracker
                        value={
                          ((Number(
                            editedDescription[entry._id] ?? entry.expenses
                          ) || 0) /
                            entry.value) *
                          100
                        }
                      />
                    )}
                  </div>
                  <div className="p-0.5 border-b border-[#dee2e6] w-[6%] flex justify-center items-center">
                    {loadingRows[index] ? (
                      <LoadingSpinner />
                    ) : (
                      <Checkbox
                        disabled={
                          isViewOnly ||
                          row.Assignedperson?.every(
                            (person) => person.isProjectComplete === true
                          ) ||
                          ((Number(
                            editedDescription[entry._id] ?? entry.expenses
                          ) || 0) /
                            entry.value) *
                          100 <
                          1
                        }
                        checked={isGroupComplete}
                        onChange={(e) => {
                          if (isViewOnly) return;
                          const isChecked = e.target.checked;
                          const currentExpenses = Number(
                            editedDescription[entry._id] ?? entry.expenses
                          );
                          const startDate =
                            updatedDateDescription[entry._id]?.startdate ||
                            entry.startdate;
                          const endDate =
                            updatedDateDescription[entry._id]?.enddate ||
                            entry.enddate;

                          if (isChecked) {
                            if (!startDate || !endDate) {
                              enqueueSnackbar("Please Select End Date", {
                                variant: "error",
                              });
                              return;
                            }

                            if (!currentExpenses || currentExpenses <= 0) {
                              enqueueSnackbar("Please Enter Expense", {
                                variant: "error",
                              });
                              return;
                            }

                            // Consolidated: mark all entries in group (manager + operation person same description)
                            handlemarkDescriptionCompleteForGroup(entriesInGroup, index);
                            handleDescriptionDateSubmit(entry);
                          } else {
                            handlemarkNotDescriptionCompleteForGroup(entriesInGroup, index);
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              );
              })
            ) : (
              <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                <p className="text-red-500 text-center">
                  {" "}
                  No Tracking Task Entries Found
                </p>
              </div>
            )}
          </div>
        )}
      </>

    </div>
  );
};

export default SelfTaskTrackManager;
